import React, { useState } from 'react';
import { FileText, User, Mail, Lock, Calendar, Users, Key, CheckCircle, ArrowRight } from 'lucide-react';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import Select from '../../components/atoms/Select';
import InsuranceNavbar from '../../components/InsuranceNavbar';
import InsuranceFooter from '../../components/InsuranceFooter';
import toast from 'react-hot-toast';

interface FormData {
  companyName: string;
  operatingCountry: string;
  licenseNumber: string;
  contactPersonName: string;
  corporateEmail: string;
  password: string;
  confirmPassword: string;
  subscriptionPlan: string;
  insuredCount: string;
  insuranceTypes: string[];
  goLiveDate: string;
  termsAccepted: boolean;
}

const CorporateAccountSetup: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [apiKey, setApiKey] = useState<{ clientId: string; secretKey: string } | null>(null);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    operatingCountry: '',
    licenseNumber: '',
    contactPersonName: '',
    corporateEmail: '',
    password: '',
    confirmPassword: '',
    subscriptionPlan: '',
    insuredCount: '',
    insuranceTypes: [],
    goLiveDate: '',
    termsAccepted: false,
  });

  // Pre-approved insurance companies
  const insuranceCompanies = [
    { value: 'golden-falcon', label: 'Golden Falcon Insurance' },
    { value: 'emirates-health', label: 'Emirates Health Insurance' },
    { value: 'dubai-national', label: 'Dubai National Insurance' },
    { value: 'abu-dhabi-shield', label: 'Abu Dhabi Shield Insurance' },
    { value: 'saudi-healthcare', label: 'Saudi Healthcare Insurance' },
    { value: 'gulf-care', label: 'Gulf Care Insurance Group' },
  ];

  const countries = [
    { value: 'uae', label: 'United Arab Emirates' },
    { value: 'saudi', label: 'Saudi Arabia' },
    { value: 'qatar', label: 'Qatar' },
    { value: 'kuwait', label: 'Kuwait' },
    { value: 'bahrain', label: 'Bahrain' },
    { value: 'oman', label: 'Oman' },
  ];

  const subscriptionPlans = [
    { value: 'analytics', label: 'HIRSTO Analytics' },
    { value: 'efficiency', label: 'HIRSTO Efficiency' },
    { value: 'strategic', label: 'HIRSTO Strategic Partner' },
  ];

  const insuranceTypeOptions = [
    { value: 'b2b-health', label: 'B2B Health Insurance' },
    { value: 'individual-health', label: 'Individual Health Insurance' },
    { value: 'family-health', label: 'Family Health Insurance' },
    { value: 'corporate-wellness', label: 'Corporate Wellness Programs' },
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleInsuranceTypeToggle = (type: string) => {
    setFormData(prev => ({
      ...prev,
      insuranceTypes: prev.insuranceTypes.includes(type)
        ? prev.insuranceTypes.filter(t => t !== type)
        : [...prev.insuranceTypes, type]
    }));
  };

  const generateApiKey = () => {
    const clientId = `HIRSTO_${Math.random().toString(36).substring(2, 15).toUpperCase()}`;
    const secretKey = `sk_live_${Math.random().toString(36).substring(2, 25)}${Math.random().toString(36).substring(2, 25)}`;
    
    setApiKey({ clientId, secretKey });
    toast.success('API Keys generated successfully!');
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleSubmit = () => {
    if (!formData.termsAccepted) {
      toast.error('Please accept the terms of use');
      return;
    }
    
    toast.success('Registration completed successfully!');
    // Navigate to dashboard
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Company Information</h3>
        <p className="text-sm text-gray-600">Please provide your official company details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Official Insurance Company Name"
          options={insuranceCompanies}
          value={formData.companyName}
          onChange={(value) => handleInputChange('companyName', value)}
          placeholder="Select your company"
          required
        />

        <Select
          label="Primary Operating Country"
          options={countries}
          value={formData.operatingCountry}
          onChange={(value) => handleInputChange('operatingCountry', value)}
          placeholder="Select country"
          required
        />

        <Input
          label="Commercial License Number"
          type="text"
          value={formData.licenseNumber}
          onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
          placeholder="Enter license number"
          icon={FileText}
          required
        />

        <Input
          label="Contact Person Name"
          type="text"
          value={formData.contactPersonName}
          onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
          placeholder="e.g., Mr. Mansour"
          icon={User}
          required
        />

        <Input
          label="Corporate Email"
          type="email"
          value={formData.corporateEmail}
          onChange={(e) => handleInputChange('corporateEmail', e.target.value)}
          placeholder="email@company.com"
          icon={Mail}
          required
        />

        <Input
          label="Password"
          type="password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          placeholder="Strong password"
          icon={Lock}
          required
        />
      </div>

      <button
        onClick={() => setCurrentStep(2)}
        className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#235DFF] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 group"
      >
        <span>Continue to Partnership Scope</span>
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Partnership Scope & Preferences</h3>
        <p className="text-sm text-gray-600">Define your partnership details and preferences</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Approved Subscription Plan"
          options={subscriptionPlans}
          value={formData.subscriptionPlan}
          onChange={(value) => handleInputChange('subscriptionPlan', value)}
          placeholder="Select your plan"
          required
        />

        <Input
          label="Number of Insured (Pilot Phase)"
          type="number"
          value={formData.insuredCount}
          onChange={(e) => handleInputChange('insuredCount', e.target.value)}
          placeholder="e.g., 5000"
          icon={Users}
          required
        />

        <Input
          label="Target Go-Live Date"
          type="date"
          value={formData.goLiveDate}
          onChange={(e) => handleInputChange('goLiveDate', e.target.value)}
          icon={Calendar}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Primary Insurance Types <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {insuranceTypeOptions.map((type) => (
            <div
              key={type.value}
              onClick={() => handleInsuranceTypeToggle(type.value)}
              className={`
                p-4 rounded-xl border-2 cursor-pointer transition-all
                ${formData.insuranceTypes.includes(type.value)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`
                  w-5 h-5 rounded-md border-2 flex items-center justify-center
                  ${formData.insuranceTypes.includes(type.value)
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                  }
                `}>
                  {formData.insuranceTypes.includes(type.value) && (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className="font-medium text-gray-900">{type.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setCurrentStep(1)}
          className="px-8 py-4 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all"
        >
          Back
        </button>
        <button
          onClick={() => setCurrentStep(3)}
          className="bg-gradient-to-r from-[#0077B6] to-[#235DFF] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center gap-2 group"
        >
          <span>Continue to API Setup</span>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Technical Key Generation</h3>
        <p className="text-sm text-gray-600">Generate API keys for system integration</p>
      </div>

      {!apiKey ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Key className="w-10 h-10 text-purple-600" />
          </div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">Generate Your API Keys</h4>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Click the button below to generate your unique Client ID and Secret Key for technical integration
          </p>
          <button
            onClick={generateApiKey}
            className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            <Key className="w-6 h-6" />
            <span>Generate API Keys</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              API Keys Generated Successfully
            </h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Client ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={apiKey.clientId}
                    readOnly
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 font-mono text-sm"
                  />
                  <Button onClick={() => copyToClipboard(apiKey.clientId, 'Client ID')} variant="outline">
                    Copy
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Secret Key</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={apiKey.secretKey}
                    readOnly
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 font-mono text-sm"
                  />
                  <Button onClick={() => copyToClipboard(apiKey.secretKey, 'Secret Key')} variant="outline">
                    Copy
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Important:</strong> Store these keys securely. They will not be shown again.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h4 className="font-bold text-gray-900 mb-2">API Documentation</h4>
            <p className="text-sm text-gray-600 mb-4">
              Review the technical documentation for integration
            </p>
            <a
              href="https://docs.hirsto.com/api"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              View API Documentation →
            </a>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                className="mt-1 w-5 h-5 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">
                I acknowledge that I am authorized to activate technical keys for the insurance company and agree to service terms and privacy conditions related to claims data exchange
              </span>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setCurrentStep(2)}
              className="px-8 py-4 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.termsAccepted}
              className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center gap-3 ${
                formData.termsAccepted
                  ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-xl hover:scale-[1.02] cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
              }`}
            >
              <span>Finish and Login</span>
              <CheckCircle className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <InsuranceNavbar />

      {/* Setup Content */}
      <div className="min-h-screen  py-12 px-4">
        <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">HIRSTO Insurance Platform</h1>
          <p className="text-lg text-gray-600">Corporate Account Setup</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex items-center gap-2">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-bold
                    ${currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}
                  `}>
                    {step}
                  </div>
                  <span className={`text-sm font-medium ${currentStep >= step ? 'text-gray-900' : 'text-gray-500'}`}>
                    {step === 1 && 'Company Info'}
                    {step === 2 && 'Partnership'}
                    {step === 3 && 'API Setup'}
                  </span>
                </div>
                {step < 3 && (
                  <div className={`w-20 h-1 ${currentStep > step ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>
        </div>
      </div>

      {/* Footer */}
      <InsuranceFooter />
    </div>
  );
};

export default CorporateAccountSetup;
